import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Plus, ChevronRight, Search } from "lucide-react";
import { listDayPlans } from "@/lib/api";
import { Input } from "@/components/ui/input";

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  return `${WEEKDAY[d.getDay()]} ${d.getDate()} ${MONTH[d.getMonth()]} ${d.getFullYear()}`;
};

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const tomorrowIso = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const DayPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [picker, setPicker] = useState(tomorrowIso());

  useEffect(() => {
    listDayPlans()
      .then(setPlans)
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return plans;
    return plans.filter((p) => {
      const pretty = fmtDate(p.date).toLowerCase();
      return p.date.includes(needle) || pretty.includes(needle);
    });
  }, [plans, q]);

  return (
    <div className="p-6 md:p-10 max-w-5xl" data-testid="day-plans-page">
      <div className="label-xs mb-2">MODULE 07 / DAY PLANS</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.9]">
        Day plans.
      </h1>
      <p className="text-zinc-600 mt-3 max-w-2xl">
        Plan tomorrow's work in advance. Every schedule is kept permanently and
        searchable by date — so you can always look back and see what was on for
        any given day.
      </p>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link
          to={`/day-plans/${tomorrowIso()}`}
          className="group bg-[#09090b] text-white p-6 hover:bg-[#27272a] transition-colors"
          data-testid="plan-tomorrow-cta"
        >
          <div className="label-xs !text-zinc-400">Plan tomorrow</div>
          <div className="font-display text-2xl sm:text-3xl font-black tracking-tighter mt-1">
            {fmtDate(tomorrowIso())}
          </div>
          <div className="font-mono text-xs text-zinc-400 mt-3 inline-flex items-center gap-1">
            Open editor <ChevronRight size={12} />
          </div>
        </Link>

        <div className="border border-zinc-300 p-6">
          <div className="label-xs">Jump to date</div>
          <div className="flex gap-2 mt-2">
            <input
              type="date"
              value={picker}
              onChange={(e) => setPicker(e.target.value)}
              className="border border-zinc-300 px-3 h-11 font-mono text-sm tabular-nums focus:outline-none focus:border-[#09090b]"
              data-testid="day-plan-date-picker"
            />
            <Link
              to={`/day-plans/${picker}`}
              className="inline-flex items-center gap-1.5 bg-[#005bea] hover:bg-[#0048b8] text-white px-4 h-11 text-xs uppercase tracking-[0.18em] font-semibold transition-colors"
              data-testid="day-plan-jump-btn"
            >
              <CalendarDays size={13} /> Open
            </Link>
          </div>
          <div className="font-mono text-[11px] text-zinc-500 mt-3">
            Today is {fmtDate(todayIso())}
          </div>
        </div>
      </div>

      <div className="mt-10">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="label-xs">Past schedules</div>
          <div className="relative w-full max-w-xs">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
            />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search 2026-04-24 or April"
              className="pl-9 h-10 rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-[#09090b] font-mono"
              data-testid="day-plan-search"
            />
          </div>
        </div>
        {loading ? (
          <div className="font-mono text-xs text-zinc-400 uppercase tracking-[0.18em] py-10 text-center">
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="border border-dashed border-zinc-300 p-10 text-center"
            data-testid="day-plans-empty"
          >
            <CalendarDays size={20} className="mx-auto text-zinc-300 mb-3" />
            <div className="font-display text-xl font-black tracking-tighter">
              {plans.length === 0 ? "No plans yet" : "No matches"}
            </div>
            {plans.length === 0 ? (
              <p className="text-zinc-500 text-sm mt-2">
                Tap "Plan tomorrow" above to start your first day plan.
              </p>
            ) : null}
          </div>
        ) : (
          <ul className="border border-zinc-200 divide-y divide-zinc-100">
            {filtered.map((p) => (
              <li key={p.date}>
                <Link
                  to={`/day-plans/${p.date}`}
                  className="flex items-center justify-between gap-4 p-5 hover:bg-zinc-50 transition-colors"
                  data-testid={`day-plan-row-${p.date}`}
                >
                  <div className="min-w-0">
                    <div className="font-display text-2xl font-black tracking-tighter">
                      {fmtDate(p.date)}
                    </div>
                    <div className="font-mono text-[11px] text-zinc-500 mt-1">
                      {p.items_count} job{p.items_count === 1 ? "" : "s"}
                      {p.has_notes ? " · has notes" : ""}
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-zinc-400 flex-none" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default DayPlans;
